const SELECTION_KEY = 'wilgood-selected-styles'

export function normalizeSelection(value) {
  if (!Array.isArray(value)) return []
  return [...new Set(value.filter(item => typeof item === 'string').map(item => item.trim().replace(/\s+/g, ' ').slice(0, 160)).filter(Boolean))].slice(0, 24)
}

export function readSelection(storage) {
  try { return normalizeSelection(JSON.parse(storage?.getItem(SELECTION_KEY) || '[]')) } catch { return [] }
}

export function writeSelection(storage, selection) {
  try {
    if (!storage) return false
    storage.setItem(SELECTION_KEY, JSON.stringify(normalizeSelection(selection)))
    return true
  } catch { return false }
}

function initFormLoading(surface) {
  const notice = surface.querySelector('[data-form-loading]')
  const app = surface.querySelector('.official-inquiry__app')
  const slow = surface.querySelector('[data-form-slow]')
  let interval, timeout
  let ready = false
  // Shopify renders controls inside an open shadow root. Styles or the host alone
  // are not evidence of readiness. Never write into or submit the app's form.
  const hasFields = root => {
    if ([...root.querySelectorAll('input[type="email"], input[inputmode="email"]')].some(input => input.getClientRects().length > 0)) return true
    return [...root.querySelectorAll('*')].some(node => node.shadowRoot && hasFields(node.shadowRoot))
  }
  const stop = () => { clearInterval(interval); clearTimeout(timeout); interval = undefined }
  const check = () => {
    if (!app || !hasFields(app)) return
    ready = true
    notice.hidden = true
    if (slow) slow.hidden = true
    stop()
  }
  const start = () => {
    if (!notice || ready || interval !== undefined) return
    check()
    if (ready) return
    interval = setInterval(check, 500)
    timeout = setTimeout(() => { if (slow) slow.hidden = false }, 8000)
  }
  return {start, stop}
}

export function initOfficialInquiry(root = document) {
  const surface = root.querySelector('[data-official-inquiry]')
  if (!surface || surface.dataset.inquiryReady) return
  surface.dataset.inquiryReady = 'true'
  const dialog = surface.querySelector('dialog')
  const inline = surface.querySelector('[data-inquiry-inline]')
  const list = surface.querySelector('[data-inquiry-code-list]')
  const selectionBox = surface.querySelector('[data-inquiry-selection]')
  const copyButton = surface.querySelector('[data-inquiry-copy]')
  const copyText = surface.querySelector('[data-inquiry-copy-text]')
  const status = surface.querySelector('[data-inquiry-status]')
  let storage
  try { storage = window.sessionStorage } catch { /* Selection stays in memory when browser storage is blocked. */ }
  let selected = readSelection(storage)
  let returnFocus
  const formLoading = initFormLoading(surface)

  const render = () => {
    root.querySelectorAll('[data-inquiry-count]').forEach(node => { node.textContent = String(selected.length) })
    if (selectionBox) selectionBox.hidden = selected.length === 0
    if (copyText) copyText.value = selected.join('\n')
    list?.replaceChildren(...selected.map(value => {
      const item = document.createElement('li')
      const label = document.createElement('span')
      label.textContent = value
      const remove = document.createElement('button')
      remove.type = 'button'
      remove.dataset.inquiryRemove = value
      remove.textContent = 'Remove'
      remove.setAttribute('aria-label', `Remove ${value}`)
      item.append(label, remove)
      return item
    }))
  }
  const persist = () => {
    if (status) status.textContent = writeSelection(storage, selected) ? '' : 'Your selection is kept on this page only. Copy it before leaving.'
    render()
  }
  const open = trigger => {
    returnFocus = trigger || document.activeElement
    if (inline) {
      inline.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block:'start'})
      inline.focus({preventScroll:true})
    } else if (dialog && !dialog.open) {
      dialog.showModal()
      document.body.classList.add('inquiry-open')
    }
    formLoading.start()
  }
  dialog?.addEventListener('close', () => {
    formLoading.stop()
    document.body.classList.remove('inquiry-open')
    if (returnFocus?.isConnected) returnFocus.focus()
  })
  // Native dialog handles keyboard focus, including the app's own encapsulated controls.
  dialog?.addEventListener('click', event => {
    if (event.target !== dialog) return
    const rect = dialog.getBoundingClientRect()
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close()
  })
  copyButton?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(selected.join('\n'))
      if (status) status.textContent = 'Copied. Paste into Interested style codes or Inquiry message below.'
    } catch {
      copyText?.focus()
      copyText?.select()
      if (status) status.textContent = 'Select and copy the list, then paste it into the form below.'
    }
  })
  root.addEventListener('click', event => {
    const target = event.target.closest?.('[data-inquiry-open], [data-quick-contact-toggle], [data-inquiry-add], [data-inquiry-remove], [data-inquiry-close]')
    if (!target) return
    event.preventDefault()
    if (target.hasAttribute('data-inquiry-close')) { dialog?.close(); return }
    if (target.hasAttribute('data-inquiry-remove')) {
      selected = selected.filter(value => value !== target.dataset.inquiryRemove)
      persist()
      if (selected.length) copyButton?.focus()
      else (dialog?.querySelector('[data-inquiry-close]') || inline)?.focus()
      return
    }
    if (target.hasAttribute('data-inquiry-add')) {
      const value = target.dataset.productCode || target.dataset.inquiryAdd
      const candidate = normalizeSelection([value])[0]
      if (candidate && !selected.includes(candidate) && selected.length >= 24) {
        if (status) status.textContent = 'Up to 24 styles per inquiry. Remove a style to add another.'
      } else {
        selected = normalizeSelection([...selected, value])
        persist()
      }
    }
    open(target)
  })
  render()
  if (inline) formLoading.start()
  if (window.location.hash === '#InquiryDrawer' || window.location.hash === '#WilgoodContactForm') open()
}

if (typeof document !== 'undefined') initOfficialInquiry()
